import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categories } from '@/mocks/categories';
import { brands } from '@/mocks/brands';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'keji-shuma');

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeSlug);
  const categoryBrands = brands.filter((b) => b.category === activeSlug);

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: '品牌分类' },
              { label: currentCategory?.name || '' },
            ]}
          />
        </div>

        <section className="bg-background-100 py-8 md:py-12">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-8">
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
                {currentCategory?.name}
              </h1>
              <p className="text-sm text-foreground-500 max-w-xl mx-auto">
                {currentCategory?.description}
              </p>
            </div>

            <div className="flex items-center justify-center gap-2 flex-wrap mb-8">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-50 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <span className="w-4 h-4 inline-flex items-center justify-center mr-1.5">
                    <i className={`${cat.icon} text-xs`}></i>
                  </span>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            {categoryBrands.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5" data-product-shop>
                {categoryBrands.map((brand) => (
                  <div
                    key={brand.id}
                    className="bg-background-100 rounded-lg overflow-hidden transition-all duration-300 hover:bg-background-200/80"
                  >
                    <div className="relative h-52 md:h-56 overflow-hidden">
                      <img
                        src={brand.image}
                        alt={`${brand.name} - 国产品牌`}
                        title={`${brand.name} - ${brand.slogan}`}
                        className="w-full h-full object-cover object-top"
                        loading="lazy"
                      />
                      <div className="absolute top-3 left-3">
                        <span className="px-2 py-0.5 bg-primary-500 text-background-50 text-xs font-medium rounded">
                          {brand.rating} 分
                        </span>
                      </div>
                      {brand.featured && (
                        <div className="absolute top-3 right-3">
                          <span className="px-2 py-0.5 bg-accent-500 text-background-50 text-xs font-medium rounded">
                            精选
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="p-5">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="font-heading text-lg font-bold text-foreground-950">
                          {brand.name}
                        </h3>
                        <span className="text-xs text-foreground-400 whitespace-nowrap">
                          成立于 {brand.foundedYear}
                        </span>
                      </div>
                      <p className="text-xs text-primary-500 font-medium mb-2">
                        {brand.slogan}
                      </p>
                      <p className="text-sm text-foreground-600 leading-relaxed mb-3 line-clamp-3">
                        {brand.description}
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {brand.tags.map((tag) => (
                          <span
                            key={tag}
                            className="px-2.5 py-0.5 bg-secondary-100 text-secondary-800 text-xs rounded"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                      {brand.website && (
                        <a
                          href={brand.website}
                          target="_blank"
                          rel="nofollow noopener noreferrer"
                          className="inline-flex items-center gap-1 mt-3 text-xs text-primary-500 hover:text-primary-600 transition-colors duration-200 cursor-pointer"
                        >
                          访问官网
                          <i className="ri-external-link-line text-xs"></i>
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-inbox-line text-2xl text-foreground-300"></i>
                </div>
                <p className="text-foreground-500">该分类暂无品牌</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}